import React from "react";
import { IntlShape, MessageDescriptor } from "react-intl";
import { StringRenderer } from "../../../shared/renderers/table";
import { RendererProps } from "../../../shared/renderers";
import { OrchestrationCriteria, getConditionType } from "../domain/orchestrationCriteria.entity";
import { ActionKeyReverseMapping } from "../domain/globalAction.types";

const SUMMARY_MAX_CHARACTERS = 180;

type MessageDictionary = Record<string, unknown>;

function findMessage(messages: MessageDictionary, key: string): MessageDescriptor | undefined {
  const candidate = messages[key];
  return candidate && typeof candidate === "object" && "id" in candidate
    ? (candidate as MessageDescriptor)
    : undefined;
}

export function createConditionsSummaryRenderer(intl: IntlShape, messages: MessageDictionary) {
  return function ConditionsSummaryRenderer(
    props: RendererProps<OrchestrationCriteria | undefined>
  ) {
    const criteria = props.value;
    if (!criteria || !criteria.conditions || criteria.conditions.length === 0) {
      return <StringRenderer {...props} value="-" />;
    }

    const value = criteria.conditions
      .map(condition => {
        const type = getConditionType(condition);
        if (!type) return "—";
        const message = findMessage(messages, type);
        return message ? intl.formatMessage(message) : type;
      })
      .join(" | ");

    return (
      <StringRenderer
        {...props}
        value={value}
        config={{ ...props.config, maxCharacters: SUMMARY_MAX_CHARACTERS }}
      />
    );
  };
}

export function createActionsSummaryRenderer(intl: IntlShape, messages: MessageDictionary) {
  return function ActionsSummaryRenderer(props: RendererProps<Record<string, string> | undefined>) {
    const actions = props.value;
    if (!actions) {
      return <StringRenderer {...props} value="-" />;
    }

    const actionKeys = Object.keys(actions);
    if (actionKeys.length === 0) {
      return <StringRenderer {...props} value="-" />;
    }

    const value = actionKeys
      .map(actionName => {
        const key = ActionKeyReverseMapping[actionName];
        const message = findMessage(messages, key);
        return message ? intl.formatMessage(message) : actionName;
      })
      .join(" | ");

    return (
      <StringRenderer
        {...props}
        value={value}
        config={{ ...props.config, maxCharacters: SUMMARY_MAX_CHARACTERS }}
      />
    );
  };
}
